import { DOCUMENT } from '@angular/common';
import { Inject, Injectable } from '@angular/core';
import { Meta, Title } from '@angular/platform-browser';
import publicPages from '../../config/public-pages.json';

interface PageMetadata { title: string; description: string; indexable: boolean; }
const PAGES: Record<string, PageMetadata> = publicPages;
const ORIGIN = 'https://yachtapp.net';

@Injectable({ providedIn: 'root' })
export class SeoService {
  constructor(
    @Inject(DOCUMENT) private readonly document: Document,
    private readonly title: Title,
    private readonly meta: Meta
  ) {}

  update(url: string, data: Record<string, unknown> = {}): void {
    // Tracking parameters and fragments never change the canonical identity.
    const path = (url.split(/[?#]/)[0].replace(/\/+$/, '') || '/');
    const page = PAGES[path];
    const title = page?.title ?? `${data['title'] || 'Your Workspace'} | YachtApp`;
    const description = page?.description ?? String(data['description'] || 'Sign in to YachtApp to access your vessels, maintenance records and team workspace.');
    const canonical = `${ORIGIN}${path === '/' ? '/' : path}`;
    this.title.setTitle(title);
    const image = `${ORIGIN}/assets/yachtapp-logo.png`;
    const tags = {
      description,
      robots: page?.indexable ? 'index, follow' : 'noindex, follow',
      'twitter:card': 'summary',
      'twitter:title': title,
      'twitter:description': description,
      'twitter:image': image
    };
    for (const [name, content] of Object.entries(tags)) this.meta.updateTag({ name, content });
    const social = {
      'og:title': title,
      'og:description': description,
      'og:type': 'website',
      'og:site_name': 'YachtApp',
      'og:image': image
    };
    for (const [property, content] of Object.entries(social)) this.meta.updateTag({ property, content });

    // Private, token-bearing and unknown routes must not advertise canonical URLs.
    this.document.querySelectorAll('link[rel="canonical"]').forEach(link => link.remove());
    if (page) {
      const link = this.document.createElement('link');
      link.rel = 'canonical';
      link.href = canonical;
      this.document.head.appendChild(link);
      this.meta.updateTag({ property: 'og:url', content: canonical });
    } else {
      this.meta.removeTag('property="og:url"');
    }
  }
}
